import React from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useLoadboardTheme } from './loadboardTheme'

const SOURCE_LABELS = { TQL: 'TQL', JBHUNT: 'JB Hunt', LANDSTAR: 'Landstar' }

const fmtLastSeen = h => {
	if (!Number.isFinite(h) || h <= 0) return 'Last seen'
	if (h < 1) {
		const mins = Math.round(h * 60)
		return `Last seen: ${mins} ${mins === 1 ? 'min' : 'mins'}`
	}
	if (h % 24 === 0) {
		const days = h / 24
		return `Last seen: ${days} ${days === 1 ? 'day' : 'days'}`
	}
	const rounded = Number(h.toFixed(2))
	return `Last seen: ${rounded} ${rounded === 1 ? 'hr' : 'hrs'}`
}

function buildChips(filters, onChange) {
	const f = filters
	const chips = []

	const isAll = !f.source || f.source === 'all'
	const sourceLabels = isAll
		? Object.values(SOURCE_LABELS).join(', ')
		: f.source
				.split(',')
				.map(k => SOURCE_LABELS[k.trim()] || k.trim())
				.filter(Boolean)
				.join(', ')
	chips.push({
		key: 'source',
		label: `Source: ${sourceLabels}`,
		clear: isAll ? null : () => onChange({ ...f, source: 'all' }),
	})

	if (f.lastSeenHours !== '' && f.lastSeenHours != null) {
		chips.push({
			key: 'lastSeenHours',
			label: fmtLastSeen(Number(f.lastSeenHours)),
			clear: () => onChange({ ...f, lastSeenHours: '' }),
		})
	}
	if (f.maxWeight !== '' && f.maxWeight != null) {
		chips.push({
			key: 'maxWeight',
			label: `Max weight: ${Number(f.maxWeight).toLocaleString()} lbs`,
			clear: () => onChange({ ...f, maxWeight: '' }),
		})
	}
	if (f.minDistance !== '' && f.minDistance != null) {
		chips.push({
			key: 'minDistance',
			label: `Min distance: ${Number(f.minDistance).toLocaleString()} mi`,
			clear: () => onChange({ ...f, minDistance: '' }),
		})
	}
	if (f.maxDistance !== '' && f.maxDistance != null) {
		chips.push({
			key: 'maxDistance',
			label: `Max distance: ${Number(f.maxDistance).toLocaleString()} mi`,
			clear: () => onChange({ ...f, maxDistance: '' }),
		})
	}

	return chips
}

function FilterChips({ filters, onChange }) {
	const { theme } = useLoadboardTheme()
	const isLight = theme === 'light'

	const chips = buildChips(filters, onChange)
	if (chips.length === 0) return null

	const chipCls = isLight
		? 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200'
		: 'bg-sky-500/15 text-sky-200 ring-1 ring-inset ring-sky-400/30'
	const chipBtnCls = isLight
		? 'text-sky-500 hover:bg-sky-100 hover:text-sky-700'
		: 'text-sky-300 hover:bg-sky-500/30 hover:text-sky-100'

	return (
		<div className="flex items-center gap-1.5 flex-wrap">
			{chips.map(chip => (
				<span
					key={chip.key}
					className={`inline-flex items-center gap-0.5 h-[22px] ${chip.clear ? 'pl-1.5 pr-0.5' : 'px-1.5'} rounded text-[11px] font-medium ${chipCls}`}
				>
					{chip.label}
					{chip.clear && (
						<button
							type="button"
							onClick={chip.clear}
							aria-label={`Remove ${chip.label}`}
							className={`flex items-center justify-center w-3.5 h-3.5 rounded ${chipBtnCls}`}
						>
							<XMarkIcon className="h-2.5 w-2.5" strokeWidth={2.5} />
						</button>
					)}
				</span>
			))}
		</div>
	)
}

export default FilterChips
